import { FILTERS_TAB_KEY } from "@/constants";
import { FilterTypes } from "@/enums";
import { IPanelScope, ITreeItem } from "@/interfaces";
import {
  fetchFavories,
  FiltersService,
  FoldersService,
  saveFavorite,
} from "@/services";
import { FavoriteItem, ServerFavoriteItem } from "@/types";
import { useState } from "react";

export const useFavorites = (
  scope: IPanelScope,
  _tree: ITreeItem[],
  maxNumberOfFavorites: number,
) => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [activeFavorite, setActiveFavorite] = useState<string | null>(null);

  const mapServerFavorite = (f: ServerFavoriteItem): FavoriteItem | null => {
    if (!f.id) return null;
    return {
      id: f.id,
      name: f.name,
      ancestry: f.ancestry?.split("/") || [],
      parentId: f.parentId,
      has_children: f.has_children,
      children: [],
    };
  };

  const reloadFavorites = async () => {
    const f = await fetchFavories(scope);
    const result = f.items;
    const favs =
      (result?.map(mapServerFavorite).filter(Boolean) as FavoriteItem[]) || [];

    setFavorites(sortFavorites(favs));
  };

  const renameFavorite = async ({ id, name }: { id: string; name: string }) => {
    const fev = favorites.map((f) => {
      if (f.id == id) {
        f.name = name;
      }
      return f;
    });

    const sortedItem = sortFavorites(fev);
    setFavorites([...sortedItem]);
  };

  const toggleFavorite = async (treeItem: ITreeItem | FavoriteItem) => {
    const data = getNewFavorites(treeItem);
    const sortedItem = sortFavorites(data);
    setFavorites(sortedItem);
    await saveFavorites(sortedItem);
    await reloadFavorites();
  };

  const saveFavorites = async (data: FavoriteItem[]) => {
    const topLevelOnly = data
      .filter((f) => !f.parentId)
      .map((f) => ({ ...f, children: [] }));
    const sorted = sortFavorites(topLevelOnly);
    await saveFavorite(scope, sorted);
  };

  const sortFavorites = (data: FavoriteItem[]): FavoriteItem[] => {
    const sorted = data.sort((a, b) => {
      if (a.index !== undefined && b.index !== undefined) {
        return a.index - b.index;
      }

      if (a.index !== undefined) {
        return -1;
      }

      if (b.index !== undefined) {
        return 1;
      }

      return a.name.localeCompare(b.name);
    });

    return sorted;
  };

  const topLevelCount = (favs: FavoriteItem[]): number => {
    return favs.filter((f) => !f.parentId).length;
  };

  const getNewFavorites = (data: ITreeItem | FavoriteItem): FavoriteItem[] => {
    const { id } = data;

    const isAlreadyFavorited = favorites.some((f) => f.id == id);

    if (isAlreadyFavorited) {
      const existingItem = favorites.find((f) => f.id == id);
      const groupId = existingItem?.parentId || id;

      mixpanel.track("favorites", { Action: "remove-favorite" });
      return favorites.filter((f) => f.id != groupId && f.parentId != groupId);
    }

    if (topLevelCount(favorites) >= maxNumberOfFavorites) {
      return favorites;
    }

    let name = data.name;
    let has_children: boolean | undefined;
    if (isITreeItem(data)) {
      if (data?.extra?.filterType === FilterTypes.AUTO_FILTER) {
        name = data.name.replace(/\s*\(\d*\)/, "");
      } else if (data?.extra?.filterType === FilterTypes.AUTO_FILTER_ROOT) {
        has_children = true;
      }
    }

    mixpanel.track("favorites", { ACTION: "add-favorite" });

    return [
      ...favorites,
      {
        id: data.id,
        name,
        ancestry: [],
        ...(has_children ? { has_children } : {}),
      },
    ];
  };

  const isITreeItem = (item: ITreeItem | FavoriteItem): item is ITreeItem => {
    return (item as ITreeItem).extra !== undefined;
  };

  const handleActiveFavoriteChanged = (
    favoriteId: string | null,
    skipActiveFavorite = false,
  ) => {
    if (!skipActiveFavorite) {
      setActiveFavorite(favoriteId);
    }

    if (favoriteId) {
      scope.tab === FILTERS_TAB_KEY
        ? FiltersService.pushToHistory({ id: favoriteId })
        : FoldersService.pushToHistory({ id: favoriteId });
    }
  };

  return {
    scope,
    favorites,
    maxNumberOfFavorites,
    activeFavorite,
    handleActiveFavoriteChanged,
    toggleFavorite,
    reloadFavorites,
    renameFavorite,
    saveFavorites,
    sortFavorites,
    setActiveFavorite,
    topLevelCount,
  };
};
